import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import type { FailureBucket } from './types';

const COLORS: Record<FailureBucket, string> = {
  'Filament Deviation Error': '#f44336',
  'Load Cell Error': '#ffeb3b',
  'Manual Stop': '#ff9800',
  'Filament Runout': '#ffc107',
  'Several Errors': '#673ab7',
  'Unfinished Printings': '#9e9e9e',
};

interface Props {
  counts: Record<FailureBucket, number>;
  selected?: FailureBucket | null;
  onSliceClick?: (name: FailureBucket) => void;
}

interface Slice {
  name: FailureBucket;
  value: number;
  color: string;
}

const renderSliceLabel = (props: PieLabelRenderProps): string => {
  const name = String(props.name ?? '');
  const value = Number(props.value ?? 0);
  const percent = Number(props.percent ?? 0);
  return `${name} (Rate ${(percent * 100).toFixed(2)}%) ${value}`;
};

export const FailureCausesPieChart = ({ counts, selected = null, onSliceClick }: Props) => {
  const data: Slice[] = (Object.keys(COLORS) as FailureBucket[])
    .map((name) => ({ name, value: counts[name] ?? 0, color: COLORS[name] }))
    .filter((d) => d.value > 0);

  if (data.length === 0) {
    return <div>No failures in range</div>;
  }

  const total = data.reduce((acc, d) => acc + d.value, 0);

  return (
    <Box position="relative" width="100%" height={300}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={60}
            outerRadius={90}
            dataKey="value"
            nameKey="name"
            label={renderSliceLabel}
            onClick={(entry: { name?: FailureBucket }) => {
              if (onSliceClick && entry?.name) onSliceClick(entry.name);
            }}
            cursor={onSliceClick ? 'pointer' : 'default'}
          >
            {data.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.color}
                fillOpacity={selected && selected !== entry.name ? 0.35 : 1}
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
      <Box
        position="absolute"
        top="50%"
        left="50%"
        sx={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
      >
        <Typography variant="body2" fontWeight={500}>
          {`Total: ${total}`}
        </Typography>
      </Box>
    </Box>
  );
};
